import React, { useState, useRef, useEffect } from 'react';
import './VideoReels.css';

const API_BASE_URL = process.env.REACT_APP_MEDIA_API_URL || 'http://localhost:6002/api';

const VideoReels = ({ initialIndex = 0, initialReels = null, onBack = null }) => {
  const [reels, setReels] = useState(initialReels || []);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isLoading, setIsLoading] = useState(initialReels ? false : true);
  const [error, setError] = useState(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [currentFilter, setCurrentFilter] = useState('all'); // 'all', 'video', 'photo'
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchEnd, setTouchEnd] = useState({ x: 0, y: 0 });
  const videoRefs = useRef([]);
  const containerRef = useRef(null);

  // Check for location on mount (only if reels not provided)
  useEffect(() => {
    if (!initialReels) {
      checkLocationAndFetchReels();
    }
  }, []);

  // Filter reels by type
  const filteredReels = currentFilter === 'all' 
    ? reels 
    : reels.filter(reel => reel.type === currentFilter);

  // Scroll to initial reel on mount
  useEffect(() => {
    if (initialIndex > 0 && filteredReels.length > 0 && containerRef.current) {
      setTimeout(() => {
        const container = containerRef.current;
        if (container) {
          container.scrollTo({ top: initialIndex * window.innerHeight, behavior: 'smooth' });
        }
      }, 300);
    }
  }, []);

  // Reset to first reel when filter changes
  useEffect(() => {
    if (filteredReels.length > 0) {
      setCurrentIndex(0);
      const container = containerRef.current;
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [currentFilter]);

  const checkLocationAndFetchReels = async () => {
    try {
      const locationData = localStorage.getItem('zz_location');
      if (locationData) {
        const loc = JSON.parse(locationData);
        setUserLocation(loc);
        fetchReels();
      } else {
        // No location available, show prompt
        setShowLocationPrompt(true);
        setIsLoading(false);
      }
    } catch (e) {
      console.error('Error checking location:', e);
      setShowLocationPrompt(true);
      setIsLoading(false);
    }
  };

  const requestLocation = async () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setError('Geolocation not available in your browser');
      return;
    }

    try {
      setIsLoading(true);
      const pos = await new Promise((resolve, reject) => 
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 20000 })
      );
      
      const loc = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude
      };
      
      localStorage.setItem('zz_location', JSON.stringify(loc));
      setUserLocation(loc);
      setShowLocationPrompt(false);
      fetchReels();
    } catch (err) {
      setError('Unable to get location. Please enable location services.');
      setIsLoading(false);
    }
  };

  const fetchReels = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Get user location from state or localStorage
      let loc = userLocation;
      if (!loc) {
        try {
          const locationData = localStorage.getItem('zz_location');
          if (locationData) {
            loc = JSON.parse(locationData);
            setUserLocation(loc);
          }
        } catch (e) {
          console.log('No location data for filtering reels');
        }
      }
      
      // Build query params - use latitude/longitude from home page format
      let queryParams = 'limit=20';
      if (loc && loc.latitude && loc.longitude) {
        queryParams += `&lat=${loc.latitude}&lng=${loc.longitude}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/reels?${queryParams}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch reels');
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        // Transform API data to match component structure
        const transformedReels = data.data.map(reel => ({
          id: reel._id,
          type: reel.type,
          url: reel.url,
          thumbnail: reel.thumbnail,
          username: reel.username,
          location: reel.location?.name || 'Unknown location',
          likes: reel.likesCount || reel.likes?.length || 0,
          caption: reel.caption || '',
          timestamp: new Date(reel.createdAt),
          userAvatar: reel.userAvatar,
          isLiked: false // TODO: Check if current user liked this
        }));
        
        setReels(transformedReels);
      }
    } catch (error) {
      console.error('Error fetching reels:', error);
      setError('Failed to load reels. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle scroll to change active reel
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const scrollPosition = container.scrollTop;
      const reelHeight = container.clientHeight;
      const newIndex = Math.round(scrollPosition / reelHeight);
      
      if (newIndex !== currentIndex && newIndex >= 0 && newIndex < reels.length) {
        setCurrentIndex(newIndex);
        
        // Pause all videos
        videoRefs.current.forEach(video => {
          if (video) video.pause();
        });
        
        // Play current video
        if (videoRefs.current[newIndex] && reels[newIndex].type === 'video') {
          videoRefs.current[newIndex].play().catch(err => console.log('Autoplay prevented:', err));
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [currentIndex, reels.length]);

  // Auto-play first video on mount
  useEffect(() => {
    if (videoRefs.current[0] && reels[0]?.type === 'video') {
      videoRefs.current[0].play().catch(err => console.log('Autoplay prevented:', err));
    }
  }, [reels]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    
    if (!isVideo && !isImage) {
      alert('Please select a video or image file');
      return;
    }

    // Check video duration for videos
    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = async function() {
        window.URL.revokeObjectURL(video.src);
        if (video.duration > 15) {
          alert('Videos must be 15 seconds or less');
          return;
        }
        
        await uploadFile(file, 'video');
      };
      
      video.src = URL.createObjectURL(file);
    } else {
      await uploadFile(file, 'image');
    }
  };

  const uploadFile = async (file, type) => {
    setIsUploading(true);
    
    try {
      // Get JWT token or use test token for development
      let token = localStorage.getItem('zz_token');
      
      // DEVELOPMENT ONLY: Generate test token if none exists
      if (!token && process.env.NODE_ENV === 'development') {
        // Create a simple test user payload
        const testUser = {
          userId: 'test-user-' + Date.now(),
          username: 'TestUser',
          avatar: null
        };
        
        // For now, use a placeholder token (backend will need to accept this for dev)
        console.warn('⚠️ Using test token for development. Set up proper authentication for production.');
        token = 'dev_token_' + btoa(JSON.stringify(testUser));
      }
      
      if (!token) {
        alert('Please log in to upload reels');
        setIsUploading(false);
        return;
      }

      // Get user location if available
      let location = null;
      try {
        const locationData = localStorage.getItem('zz_location');
        if (locationData) {
          const loc = JSON.parse(locationData);
          // Use latitude/longitude from home page format
          if (loc.latitude && loc.longitude) {
            location = JSON.stringify({
              lat: loc.latitude,
              lng: loc.longitude
            });
          }
        }
      } catch (e) {
        console.log('No location data');
      }

      // Location is required for upload
      if (!location) {
        alert('Location is required to share reels. ZoneZapp only shows reels within your city to keep content local and relevant. Your exact location is never shared with others.');
        setIsUploading(false);
        setShowLocationPrompt(true);
        return;
      }

      // Create FormData
      const formData = new FormData();
      formData.append('media', file);
      formData.append('caption', 'Shared from ZoneZapp! 📍');
      if (location) {
        formData.append('location', location);
        formData.append('locationName', 'My Location');
      }
      
      // Upload to backend
      const response = await fetch(`${API_BASE_URL}/reels/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Upload failed');
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh reels list
        await fetchReels();
        
        setShowUploadModal(false);
        alert('Uploaded successfully! 🎉');
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLike = async (reelId) => {
    try {
      const token = localStorage.getItem('zz_token');
      
      if (!token) {
        alert('Please log in to like reels');
        return;
      }

      // Optimistic update
      setReels(reels.map(reel => 
        reel.id === reelId 
          ? { ...reel, likes: reel.isLiked ? reel.likes - 1 : reel.likes + 1, isLiked: !reel.isLiked }
          : reel
      ));

      const response = await fetch(`${API_BASE_URL}/reels/${reelId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // Revert on error
        setReels(reels.map(reel => 
          reel.id === reelId 
            ? { ...reel, likes: reel.isLiked ? reel.likes + 1 : reel.likes - 1, isLiked: !reel.isLiked }
            : reel
        ));
        throw new Error('Failed to like reel');
      }

      const data = await response.json();
      
      if (data.success) {
        // Update with actual like count from server
        setReels(reels.map(reel => 
          reel.id === reelId 
            ? { ...reel, likes: data.data.likesCount, isLiked: data.data.liked }
            : reel
        ));
      }
    } catch (error) {
      console.error('Like error:', error);
    }
  };

  const formatTimestamp = (date) => {
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // seconds
    
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const goToNextReel = () => {
    if (currentIndex < filteredReels.length - 1) {
      const container = containerRef.current;
      if (container) {
        container.scrollTo({
          top: (currentIndex + 1) * container.clientHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  const goToPrevReel = () => {
    if (currentIndex > 0) {
      const container = containerRef.current;
      if (container) {
        container.scrollTo({
          top: (currentIndex - 1) * container.clientHeight,
          behavior: 'smooth'
        });
      }
    }
  };

  const switchToVideos = () => {
    setCurrentFilter('video');
  };

  const switchToPhotos = () => {
    setCurrentFilter('photo');
  };

  const switchToAll = () => {
    setCurrentFilter('all');
  };

  // Touch handlers for swipe gestures
  const handleTouchStart = (e) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };

  const handleTouchMove = (e) => {
    setTouchEnd({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };

  const handleTouchEnd = () => {
    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = touchStart.y - touchEnd.y;
    const minSwipeDistance = 50;

    // Horizontal swipe (left/right) - switch between types
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        // Swiped left - go to videos
        if (currentFilter !== 'video') {
          switchToVideos();
        }
      } else {
        // Swiped right - go to photos
        if (currentFilter !== 'photo') {
          switchToPhotos();
        }
      }
    }
    // Vertical swipe is handled by scroll
  };

  const handleBack = () => {
    // If onBack callback is provided, use it (for embedded view)
    if (onBack) {
      onBack();
    } else {
      // Otherwise navigate to home page (for standalone view)
      window.location.href = '/webapp/home';
    }
  };

  return (
    <div className="video-reels-container">
      {/* Back Button */}
      <button 
        className="reels-back-btn" 
        onClick={handleBack}
        aria-label="Go back"
      >
        <span className="back-icon">←</span>
      </button>

      {/* Filter Pills */}
      <div className="reels-filter-pills">
        <button 
          className={`filter-pill ${currentFilter === 'all' ? 'active' : ''}`}
          onClick={switchToAll}
        >
          All ({reels.length})
        </button>
        <button 
          className={`filter-pill ${currentFilter === 'video' ? 'active' : ''}`}
          onClick={switchToVideos}
        >
          🎥 Videos ({reels.filter(r => r.type === 'video').length})
        </button>
        <button 
          className={`filter-pill ${currentFilter === 'photo' ? 'active' : ''}`}
          onClick={switchToPhotos}
        >
          📷 Photos ({reels.filter(r => r.type === 'photo').length})
        </button>
      </div>

      {/* Add Button */}
      <button 
        className="reels-add-btn" 
        onClick={() => setShowUploadModal(true)}
        aria-label="Add video or photo"
        disabled={isUploading}
      >
        <span className="add-icon">{isUploading ? '⏳' : '+'}</span>
      </button>

      {/* Horizontal Navigation Arrows (for switching types) */}
      {reels.length > 0 && (
        <>
          {currentFilter !== 'photo' && (
            <button 
              className="reels-nav-btn reels-nav-left" 
              onClick={switchToPhotos}
              aria-label="Switch to photos"
            >
              ←
            </button>
          )}
          {currentFilter !== 'video' && (
            <button 
              className="reels-nav-btn reels-nav-right" 
              onClick={switchToVideos}
              aria-label="Switch to videos"
            >
              →
            </button>
          )}
        </>
      )}

      {/* Vertical Navigation Arrows (for same type) */}
      {filteredReels.length > 1 && (
        <>
          {currentIndex > 0 && (
            <button 
              className="reels-nav-btn reels-nav-up" 
              onClick={goToPrevReel}
              aria-label="Previous reel"
            >
              ↑
            </button>
          )}
          {currentIndex < filteredReels.length - 1 && (
            <button 
              className="reels-nav-btn reels-nav-down" 
              onClick={goToNextReel}
              aria-label="Next reel"
            >
              ↓
            </button>
          )}
        </>
      )}

      {/* Reels Viewer */}
      <div 
        className="reels-viewer" 
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {isLoading ? (
          <div className="reels-empty">
            <div className="empty-icon">⏳</div>
            <h3>Loading reels...</h3>
            <p>Please wait</p>
          </div>
        ) : error ? (
          <div className="reels-empty">
            <div className="empty-icon">⚠️</div>
            <h3>Error loading reels</h3>
            <p>{error}</p>
            <button onClick={fetchReels} className="empty-upload-btn">
              Try Again
            </button>
          </div>
        ) : filteredReels.length === 0 ? (
          <div className="reels-empty">
            <div className="empty-icon">📹</div>
            <h3>No {currentFilter === 'all' ? 'reels' : currentFilter + 's'} yet</h3>
            <p>{currentFilter === 'all' ? 'Be the first to share a moment!' : `No ${currentFilter}s available. Try switching filters.`}</p>
            <button onClick={() => setShowUploadModal(true)} className="empty-upload-btn">
              Upload Video or Photo
            </button>
          </div>
        ) : (
          filteredReels.map((reel, index) => (
            <div key={reel.id} className="reel-item">
              {reel.type === 'video' ? (
                <video
                  ref={el => videoRefs.current[index] = el}
                  className="reel-video"
                  src={reel.url}
                  loop
                  playsInline
                  muted={false}
                  poster={reel.thumbnail}
                />
              ) : (
                <img
                  src={reel.url}
                  alt={reel.caption}
                  className="reel-image"
                />
              )}

              {/* Overlay Info */}
              <div className="reel-overlay">
                <div className="reel-info">
                  <div className="reel-user">
                    <div className="user-avatar">{reel.username[0].toUpperCase()}</div>
                    <div>
                      <div className="username">@{reel.username}</div>
                      <div className="timestamp">{formatTimestamp(reel.timestamp)}</div>
                    </div>
                  </div>
                  
                  <p className="reel-caption">{reel.caption}</p>
                  
                  {reel.location && (
                    <div className="reel-location">
                      📍 {reel.location.name || 'Unknown location'}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="reel-actions">
                  <button 
                    className={`action-btn ${reel.isLiked ? 'liked' : ''}`}
                    onClick={() => handleLike(reel.id)}
                    aria-label="Like"
                  >
                    <span className="action-icon">{reel.isLiked ? '❤️' : '🤍'}</span>
                    <span className="action-count">{reel.likes}</span>
                  </button>
                  
                  <button className="action-btn" aria-label="Comment">
                    <span className="action-icon">💬</span>
                  </button>
                  
                  <button className="action-btn" aria-label="Share">
                    <span className="action-icon">📤</span>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="upload-modal" onClick={e => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h3>Share a Moment</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowUploadModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="upload-modal-body">
              <label className="upload-option">
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileSelect}
                  disabled={isUploading}
                  style={{ display: 'none' }}
                />
                <div className="option-card">
                  <span className="option-icon">🎥</span>
                  <h4>Video</h4>
                  <p>15 seconds max</p>
                </div>
              </label>

              <label className="upload-option">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={isUploading}
                  style={{ display: 'none' }}
                />
                <div className="option-card">
                  <span className="option-icon">📷</span>
                  <h4>Photo</h4>
                  <p>Share a picture</p>
                </div>
              </label>
            </div>

            {isUploading && (
              <div className="upload-progress">
                <div className="spinner"></div>
                <p>Uploading...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Location Permission Prompt */}
      {showLocationPrompt && (
        <div className="upload-modal-overlay">
          <div className="upload-modal location-prompt">
            <div className="upload-modal-header">
              <h3>📍 Location Required</h3>
            </div>
            
            <div className="upload-modal-body">
              <p style={{ marginBottom: '15px', lineHeight: '1.6' }}>
                <strong>ZoneZapp needs your location to show you reels from your city.</strong>
              </p>
              <p style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
                🔒 <strong>Your privacy is protected:</strong>
              </p>
              <ul style={{ fontSize: '14px', color: '#666', marginBottom: '20px', paddingLeft: '20px' }}>
                <li>We only use your city-level location (within 50km)</li>
                <li>Your exact location is never shared with other users</li>
                <li>Location is used only to filter reels to your area</li>
              </ul>
              
              <button 
                className="upload-btn" 
                onClick={requestLocation}
                disabled={isLoading}
                style={{ width: '100%', marginBottom: '10px' }}
              >
                {isLoading ? 'Getting Location...' : 'Allow Location'}
              </button>
              
              {error && (
                <p style={{ color: 'red', fontSize: '14px', marginTop: '10px' }}>
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoReels;
